type SelectionsMap<TType extends string> = Record<TType, string[]>;
type SetSelectionsMap<TType extends string> = Record<TType, (next: string[]) => void>;

type CreateFilterSelectionActionsInput<TType extends string> = {
  selections: SelectionsMap<TType>;
  setSelections: SetSelectionsMap<TType>;
  mapIncomingValue?: (type: TType, rawValue: string) => string;
  onBeforeMutate?: () => void;
  onAfterMutate: (nextSelections: SelectionsMap<TType>, changedType: TType) => void;
};

const defaultMapper = <TType extends string>(_: TType, rawValue: string) => rawValue;

export const createFilterSelectionActions = <TType extends string>({
  selections,
  setSelections,
  mapIncomingValue = defaultMapper,
  onBeforeMutate,
  onAfterMutate,
}: CreateFilterSelectionActionsInput<TType>) => {
  const toggleSelection = (type: TType, rawValue: string) => {
    const mappedValue = mapIncomingValue(type, rawValue);
    const selectedForType = selections[type] ?? [];
    const nextForType = selectedForType.includes(mappedValue)
      ? selectedForType.filter((item) => item !== mappedValue)
      : [...selectedForType, mappedValue];

    const nextSelections = { ...selections, [type]: nextForType } as SelectionsMap<TType>;
    onBeforeMutate?.();
    setSelections[type](nextForType);
    onAfterMutate(nextSelections, type);
  };

  const clearSelection = (type: TType) => {
    const nextSelections = { ...selections, [type]: [] } as SelectionsMap<TType>;
    onBeforeMutate?.();
    setSelections[type]([]);
    onAfterMutate(nextSelections, type);
  };

  return { toggleSelection, clearSelection };
};
